// Envío a mano desde la pestaña: en crédito elige momento y canal; en venta solo reenvía el código
// de referido. El éxito avisa con un toast y el error se queda junto al botón.
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { DeliveryChannelExtendedEnum } from '@fiado/type-kit/bin/messagesConnector/enums/DeliveryChannelExtendedEnum';
import { Button, usePermissions } from '@/kit';
import { cn } from '@/lib/cn';
import {
    COPY,
    CREDIT_MANUAL_MOMENTS,
    CUSTOMER_MESSAGES_PERMISSIONS,
    MANUAL_CHANNELS,
    MessageMomentCategoryEnum,
    sendErrorMessageOf,
    type ManualChannel,
} from './customerMessages';
import {
    useResendReferralCoupon,
    useSendCreditManualMessage,
    useTemplatePreview,
} from './useCustomerMessagesData';

const OPTION_CLASS =
    'rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function OptionGroup({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div role="radiogroup" aria-label={label} className="mb-2">
            <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
                {label}
            </p>
            <div className="flex flex-wrap gap-1">{children}</div>
        </div>
    );
}

function Option({
    selected,
    onSelect,
    children,
}: {
    selected: boolean;
    onSelect: () => void;
    children: string;
}) {
    return (
        <button
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={onSelect}
            className={cn(
                OPTION_CLASS,
                selected
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-card text-muted-foreground hover:text-foreground',
            )}
        >
            {children}
        </button>
    );
}

function SendError({ error }: { error: unknown }) {
    if (!error) return null;
    return (
        <p role="alert" className="mt-2 text-[11px] text-destructive">
            {sendErrorMessageOf(error)}
        </p>
    );
}

function CreditSendForm({
    retailCustomerId,
    onDone,
}: {
    retailCustomerId: string;
    onDone: () => void;
}) {
    const { hasPermission } = usePermissions();
    const [templateId, setTemplateId] = useState(CREDIT_MANUAL_MOMENTS[0]?.templateId ?? '');
    const [channel, setChannel] = useState<ManualChannel>(DeliveryChannelExtendedEnum.SMS);
    const moment = CREDIT_MANUAL_MOMENTS.find((m) => m.templateId === templateId) ?? null;
    const preview = useTemplatePreview(
        moment?.messageType ?? null,
        channel,
        hasPermission(CUSTOMER_MESSAGES_PERMISSIONS.templateRead),
    );
    const send = useSendCreditManualMessage(retailCustomerId, MessageMomentCategoryEnum.CREDIT);

    return (
        <>
            <OptionGroup label={COPY.templateLabel}>
                {CREDIT_MANUAL_MOMENTS.map((m) => (
                    <Option
                        key={m.templateId}
                        selected={m.templateId === templateId}
                        onSelect={() => {
                            setTemplateId(m.templateId);
                        }}
                    >
                        {`${m.name} · ${m.templateId}`}
                    </Option>
                ))}
            </OptionGroup>
            <OptionGroup label={COPY.channelLabel}>
                {MANUAL_CHANNELS.map((c) => (
                    <Option
                        key={c.value}
                        selected={c.value === channel}
                        onSelect={() => {
                            setChannel(c.value);
                        }}
                    >
                        {c.label}
                    </Option>
                ))}
            </OptionGroup>
            {preview.data ? (
                <figure className="mb-2 rounded-md border border-border bg-muted/30 px-2.5 py-2">
                    <blockquote className="whitespace-pre-wrap text-[11.5px] leading-snug">
                        {preview.data}
                    </blockquote>
                    <figcaption className="mt-1 text-[10px] text-muted-foreground">
                        {COPY.variablesNote}
                    </figcaption>
                </figure>
            ) : null}
            {preview.isError ? (
                <p className="mb-2 text-[11px] text-amber-600">
                    {COPY.previewError}
                </p>
            ) : null}
            <Button
                size="sm"
                isLoading={send.isPending}
                disabled={moment === null || send.isPending}
                onClick={() => {
                    send.mutate(
                        { templateId, channel },
                        {
                            onSuccess: () => {
                                toast.success(COPY.sendSuccess);
                                onDone();
                            },
                        },
                    );
                }}
            >
                {COPY.send}
            </Button>
            <SendError error={send.error} />
        </>
    );
}

function SaleSendForm({
    retailCustomerId,
    onDone,
}: {
    retailCustomerId: string;
    onDone: () => void;
}) {
    const resend = useResendReferralCoupon(retailCustomerId, MessageMomentCategoryEnum.SALE);
    return (
        <>
            <p className="mb-2 text-[11px] text-muted-foreground">{COPY.resendCouponNote}</p>
            <Button
                size="sm"
                isLoading={resend.isPending}
                disabled={resend.isPending}
                onClick={() => {
                    resend.mutate(undefined, {
                        onSuccess: () => {
                            toast.success(COPY.sendSuccess);
                            onDone();
                        },
                    });
                }}
            >
                {COPY.resendCoupon}
            </Button>
            <SendError error={resend.error} />
        </>
    );
}

/** Botón «Enviar mensaje» que abre el panel; `null` si el rol no puede enviar en esta categoría. */
export function ManualMessagePanel({
    retailCustomerId,
    category,
}: {
    retailCustomerId: string;
    category: MessageMomentCategoryEnum;
}) {
    const { hasPermission } = usePermissions();
    const [open, setOpen] = useState(false);
    const permission =
        category === MessageMomentCategoryEnum.CREDIT
            ? CUSTOMER_MESSAGES_PERMISSIONS.creditManualSend
            : CUSTOMER_MESSAGES_PERMISSIONS.referralCouponResend;
    if (!hasPermission(permission)) return null;

    const close = () => {
        setOpen(false);
    };
    if (!open) {
        return (
            <div className="mb-2 flex justify-end">
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                        setOpen(true);
                    }}
                >
                    {COPY.sendOpen}
                </Button>
            </div>
        );
    }
    return (
        <section
            aria-label={COPY.sendOpen}
            className="mb-2.5 rounded-md border border-border bg-card px-3 py-2.5"
        >
            <div className="mb-2 flex items-center justify-between">
                <h4 className="text-[12px] font-semibold">{COPY.sendOpen}</h4>
                <button
                    type="button"
                    onClick={close}
                    className="text-[10.5px] text-muted-foreground hover:text-foreground"
                >
                    {COPY.sendClose}
                </button>
            </div>
            {category === MessageMomentCategoryEnum.CREDIT ? (
                <CreditSendForm retailCustomerId={retailCustomerId} onDone={close} />
            ) : (
                <SaleSendForm retailCustomerId={retailCustomerId} onDone={close} />
            )}
        </section>
    );
}
